import { useAuth0 } from "@auth0/auth0-react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { getIdList } from "@utils/helpers";
import clsx from "clsx";
import React, { useMemo, useState } from "react";

import {
  Button,
  CheckboxList,
  Headline,
  Input,
  Label,
  MultiSelect,
  MultiSelectItem,
  Paragraph,
  Select,
  SelectItem,
  Small,
} from "@fronterahealth/frontera-ui-components";

import {
  CaregiverType,
  ProviderType,
  useCreateLearnerCaregiverMutation,
  useGetProvidersForAssigningToLearnerQuery,
  useLearnersQuery,
  useSendCaregiverActivationCodeNotificationMutation,
  useUpdateLearnerCaregiverDetailsMutation,
  useUpdateLearnerProvidersMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useAllCaregivers } from "@hooks/useAllCaregivers";
import { CaregiverAppStatus, MemberCaregiver } from "@pages/Members/types";
import { buildBcbaNameByProviderId, mapCaregiverToMemberCaregiver } from "@pages/Members/utils/caregiverMapping";
import { useAdminData } from "@providers/AdminDataProvider";
import { useProvidersData } from "@providers/ProvidersDataProvider";

interface InviteCaregiverModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedCaregiverId?: string;
}

enum Step {
  Select = "select",
  Manual = "manual",
  Confirm = "confirm",
}

interface ManualFormData {
  caregiverFirstName: string;
  caregiverLastName: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  learnerFirstName: string;
  learnerLastName: string;
  birthDate: string;
}

const EMPTY_MANUAL_FORM: ManualFormData = {
  caregiverFirstName: "",
  caregiverLastName: "",
  email: "",
  phone: "",
  isPrimary: true,
  learnerFirstName: "",
  learnerLastName: "",
  birthDate: "",
};

enum ChildMode {
  New = "new",
  Existing = "existing",
}

function getDeliveryMethod(email: string, phone: string): string {
  const hasEmail = Boolean(email);
  const hasPhone = Boolean(phone);
  if (hasEmail && hasPhone) return "Activation code will be sent by email and SMS";
  if (hasEmail) return "Activation code will be sent by email";
  if (hasPhone) return "Activation code will be sent by SMS";
  return "No contact info on file. The activation code will be generated for you to share manually.";
}

export const InviteCaregiverModal: React.FC<InviteCaregiverModalProps> = ({
  isOpen,
  onClose,
  preselectedCaregiverId,
}) => {
  const { isAuthenticated } = useAuth0();
  const { roles, orgId } = useAdminData();
  const [step, setStep] = useState<Step>(Step.Select);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCaregiver, setSelectedCaregiver] = useState<MemberCaregiver | null>(null);
  const [copied, setCopied] = useState(false);
  const [manualForm, setManualForm] = useState<ManualFormData>(EMPTY_MANUAL_FORM);
  const [cameFromManual, setCameFromManual] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState<MultiSelectItem[]>([]);
  const [childMode, setChildMode] = useState<ChildMode>(ChildMode.New);
  const [selectedLearnerItem, setSelectedLearnerItem] = useState<SelectItem | null>(null);

  const { providersQuery } = useProvidersData();
  const queryClient = useQueryClient();

  const {
    caregivers: allCaregivers,
    isLoading: caregiversLoading,
    isFetchingMore: caregiversFetchingMore,
  } = useAllCaregivers({ enabled: isAuthenticated && roles.includes("admin") });

  const refetchCaregivers = () => queryClient.invalidateQueries({ queryKey: ["getCaregiversDataQuery"] });

  const providersForAssignmentQuery = useGetProvidersForAssigningToLearnerQuery(
    {},
    {
      queryKey: ["getProvidersForAssigningToLearner"],
      enabled: isAuthenticated && roles.includes("admin"),
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const assignableLearnerProviders = useMemo<MultiSelectItem[]>(
    () =>
      providersForAssignmentQuery.data?.getProvidersForAssigningToLearner?.edges.map((edge) => ({
        id: edge?.node?.id || "",
        primary: `${edge?.node?.providermetadata?.firstName || "N/A"} ${edge?.node?.providermetadata?.lastName || ""} (${edge?.node?.rolesNameOnly?.map((item) => item?.toUpperCase()).join(",")})`,
        secondary: `${edge?.node?.providermetadata?.email}`,
      })) || [],
    [providersForAssignmentQuery.data],
  );

  const learnersQuery = useLearnersQuery(
    {},
    {
      queryKey: ["learners", isAuthenticated, JSON.stringify(roles)],
      retry: false,
      enabled:
        isAuthenticated &&
        !!roles.length &&
        (roles.includes("admin") || roles.includes("bcba") || roles.includes("assessment-bcba")),
      refetchOnWindowFocus: false,
    },
  );

  const allLearners = useMemo(() => (learnersQuery.data?.learner || []).filter(Boolean), [learnersQuery.data]);

  const learnerItems = useMemo<SelectItem[]>(
    () =>
      allLearners
        .map((l) => ({
          id: l!.id,
          primary: `${l!.learnermetadata?.firstName || ""} ${l!.learnermetadata?.lastName || ""}`.trim(),
          secondary: "",
        }))
        .sort((a, b) => (a.primary || "").localeCompare(b.primary || "")),
    [allLearners],
  );

  const selectedLearner = useMemo(
    () => allLearners.find((l) => l?.id === selectedLearnerItem?.id),
    [allLearners, selectedLearnerItem],
  );

  const selectedLearnerHasPrimaryCaregiver = useMemo(
    () => selectedLearner?.relationshipSet?.edges?.some((edge) => edge?.node?.isPrimary) ?? false,
    [selectedLearner],
  );

  // Pre-populate providers with the child's current set — updateLearnerProviders replaces the full set.
  const [prevProviderSyncInputs, setPrevProviderSyncInputs] = useState({
    childMode,
    selectedLearner,
    assignableLearnerProviders,
  });

  if (
    childMode !== prevProviderSyncInputs.childMode ||
    selectedLearner !== prevProviderSyncInputs.selectedLearner ||
    assignableLearnerProviders !== prevProviderSyncInputs.assignableLearnerProviders
  ) {
    setPrevProviderSyncInputs({ childMode, selectedLearner, assignableLearnerProviders });
    if (childMode === ChildMode.Existing) {
      const currentProviderIds = new Set(
        (selectedLearner?.learnerproviderSet?.edges || [])
          .map((edge) => edge?.node?.provider?.id)
          .filter((id): id is string => !!id),
      );
      setSelectedProviders(assignableLearnerProviders.filter((p) => p.id && currentProviderIds.has(p.id)));
    }
  }

  const bcbaNameByProviderId = useMemo(
    () => buildBcbaNameByProviderId((providersQuery.data?.provider || []) as ProviderType[]),
    [providersQuery.data],
  );

  const caregivers = useMemo(
    () =>
      allCaregivers
        .map((caregiver) => mapCaregiverToMemberCaregiver(caregiver, bcbaNameByProviderId))
        .filter((c): c is MemberCaregiver => c !== null),
    [allCaregivers, bcbaNameByProviderId],
  );

  const sendActivationCode = useSendCaregiverActivationCodeNotificationMutation({
    onSuccess: (res) => {
      if (!res.sendCaregiverActivationCodeNotification?.status) {
        notifyError(res.sendCaregiverActivationCodeNotification?.message || "Something went wrong sending the code.");
        return;
      }
      notifySuccess(res.sendCaregiverActivationCodeNotification?.message || "Activation code sent.");
      refetchCaregivers();
      onClose();
    },
    onError: () => {
      notifyError("Something went wrong sending the activation code.");
    },
  });

  const createLearnerCaregiver = useCreateLearnerCaregiverMutation({
    onSuccess: (res) => {
      const created = res.createLearnerCaregiver;
      if (!created?.status || !created.caregiver) {
        notifyError(created?.message || "Something went wrong creating this caregiver.");
        return;
      }
      const mapped = mapCaregiverToMemberCaregiver(
        created.caregiver as CaregiverType | undefined,
        bcbaNameByProviderId,
      );
      if (!mapped) {
        notifyError("Something went wrong creating this caregiver.");
        return;
      }
      refetchCaregivers();
      setSelectedCaregiver(mapped);
      setCameFromManual(true);
      setStep(Step.Confirm);
    },
    onError: () => {
      notifyError("Something went wrong creating this caregiver.");
    },
  });

  const updateLearnerProviders = useUpdateLearnerProvidersMutation({
    onError: () => {
      notifyError("Caregiver was created, but something went wrong updating this child's providers.");
    },
  });

  const linkCaregiverToExistingLearner = useUpdateLearnerCaregiverDetailsMutation({
    onSuccess: (res) => {
      const result = res.updateLearnerCaregiverDetails;
      if (!result?.status || !result.caregiverData) {
        notifyError(result?.message || "Something went wrong creating this caregiver.");
        return;
      }
      const mapped = mapCaregiverToMemberCaregiver(
        result.caregiverData as CaregiverType | undefined,
        bcbaNameByProviderId,
      );
      if (!mapped) {
        notifyError("Something went wrong creating this caregiver.");
        return;
      }
      if (selectedLearnerItem?.id) {
        updateLearnerProviders.mutate({
          learnerId: selectedLearnerItem.id,
          providerIds: getIdList(selectedProviders),
        });
      }
      refetchCaregivers();
      learnersQuery.refetch();
      setSelectedCaregiver(mapped);
      setCameFromManual(true);
      setStep(Step.Confirm);
    },
    onError: () => {
      notifyError("Something went wrong creating this caregiver.");
    },
  });

  // Reset state when modal opens/closes. Adjusted during render (not an effect)
  // per React's "adjusting state when a prop changes" pattern.
  const resetKey = `${isOpen}:${preselectedCaregiverId ?? ""}`;
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    if (isOpen) {
      setCopied(false);
      setStep(Step.Select);
      setSearchQuery("");
      setSelectedCaregiver(null);
      setManualForm(EMPTY_MANUAL_FORM);
      setSelectedProviders([]);
      setChildMode(ChildMode.New);
      setSelectedLearnerItem(null);
      setCameFromManual(false);
    }
  }

  // Jump to confirm once the caregiver list loads far enough to resolve preselectedCaregiverId.
  const [prevPreselectSyncInputs, setPrevPreselectSyncInputs] = useState({
    isOpen,
    preselectedCaregiverId,
    caregivers,
    caregiversLoading,
  });
  if (
    isOpen !== prevPreselectSyncInputs.isOpen ||
    preselectedCaregiverId !== prevPreselectSyncInputs.preselectedCaregiverId ||
    caregivers !== prevPreselectSyncInputs.caregivers ||
    caregiversLoading !== prevPreselectSyncInputs.caregiversLoading
  ) {
    setPrevPreselectSyncInputs({ isOpen, preselectedCaregiverId, caregivers, caregiversLoading });
    if (isOpen && preselectedCaregiverId && !caregiversLoading) {
      const caregiver = caregivers.find((c) => c.id === preselectedCaregiverId);
      if (caregiver) {
        setSelectedCaregiver(caregiver);
        setStep(Step.Confirm);
      }
    }
  }

  const isResolvingPreselectedCaregiver =
    isOpen && !!preselectedCaregiverId && step === Step.Select && (caregiversLoading || caregiversFetchingMore);

  const filteredCaregivers = useMemo(() => {
    if (!searchQuery.trim()) return caregivers;
    const q = searchQuery.toLowerCase();
    return caregivers.filter(
      (c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.phone.includes(q),
    );
  }, [caregivers, searchQuery]);

  const isInvitable = (status: CaregiverAppStatus) =>
    status === CaregiverAppStatus.NotInvited || status === CaregiverAppStatus.CodeSent;

  const statusLabel = (status: CaregiverAppStatus) => {
    switch (status) {
      case CaregiverAppStatus.CodeSent:
        return "Code sent";
      case CaregiverAppStatus.Connected:
        return "Connected";
      default:
        return null;
    }
  };

  const handleSelectCaregiver = (caregiver: MemberCaregiver) => {
    setSelectedCaregiver(caregiver);
    setStep(Step.Confirm);
  };

  const handleCopyCode = async () => {
    if (!selectedCaregiver?.activationCode) return;
    try {
      await navigator.clipboard.writeText(selectedCaregiver.activationCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: silently fail in environments without clipboard API
    }
  };

  const handleSendInvite = () => {
    if (!selectedCaregiver || !orgId) return;
    sendActivationCode.mutate({ caregiverId: selectedCaregiver.id, organizationId: orgId });
  };

  const isManualCaregiverInfoValid = !!manualForm.caregiverFirstName && !!manualForm.caregiverLastName;

  const isManualFormValid =
    childMode === ChildMode.New
      ? isManualCaregiverInfoValid &&
        !!manualForm.learnerFirstName &&
        !!manualForm.learnerLastName &&
        !!manualForm.birthDate
      : isManualCaregiverInfoValid && !!selectedLearnerItem;

  const isManualMutationPending = createLearnerCaregiver.isPending || linkCaregiverToExistingLearner.isPending;

  const handleManualNext = () => {
    if (!isManualFormValid) return;

    if (childMode === ChildMode.Existing) {
      if (!selectedLearnerItem) return;
      linkCaregiverToExistingLearner.mutate({
        learner: selectedLearnerItem.id!,
        caregiverDetails: {
          firstName: manualForm.caregiverFirstName,
          lastName: manualForm.caregiverLastName,
          email: manualForm.email || undefined,
          phoneNumber1: manualForm.phone || undefined,
          isPrimary: selectedLearnerHasPrimaryCaregiver ? false : manualForm.isPrimary,
        },
      });
      return;
    }

    createLearnerCaregiver.mutate({
      learner: {
        firstName: manualForm.learnerFirstName,
        lastName: manualForm.learnerLastName,
        birthDate: manualForm.birthDate,
      },
      caregiver: {
        firstName: manualForm.caregiverFirstName,
        lastName: manualForm.caregiverLastName,
        email: manualForm.email || undefined,
        phoneNumber1: manualForm.phone || undefined,
        isPrimary: manualForm.isPrimary,
      },
      sendIntake: false,
      providerIds: selectedProviders.length ? getIdList(selectedProviders) : undefined,
    });
  };

  const handleBack = () => {
    if (step === Step.Manual) {
      setStep(Step.Select);
      return;
    }
    if (step === Step.Confirm && !preselectedCaregiverId && !cameFromManual) {
      setStep(Step.Select);
      setSelectedCaregiver(null);
    } else {
      onClose();
    }
  };

  const isMissingContact = (caregiver: MemberCaregiver) => !caregiver.email && !caregiver.phone;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <Headline as="h2" size="small">
            {step === Step.Select && "Invite a caregiver"}
            {step === Step.Manual && "Add caregiver manually"}
            {step === Step.Confirm && "Confirm invite"}
          </Headline>
          <button
            onClick={onClose}
            className="cursor-pointer text-text-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Step 1: Select caregiver */}
        {step === Step.Select && isResolvingPreselectedCaregiver && (
          <Paragraph colorType="secondary" className="py-4 text-center">
            Loading caregiver…
          </Paragraph>
        )}
        {step === Step.Select && !isResolvingPreselectedCaregiver && (
          <div className="flex flex-col gap-4">
            <Input
              name="caregiverSearch"
              label=""
              formNoValidate
              inputSize="full"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div className="max-h-64 overflow-y-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
              {filteredCaregivers.length === 0 ? (
                <Paragraph colorType="secondary" className="py-4 text-center">
                  No caregivers found
                </Paragraph>
              ) : (
                <ul className="divide-y divide-limestone-100">
                  {filteredCaregivers.map((caregiver) => {
                    const canInvite = isInvitable(caregiver.appStatus);
                    const label = statusLabel(caregiver.appStatus);
                    return (
                      <li key={caregiver.id}>
                        <button
                          onClick={() => canInvite && handleSelectCaregiver(caregiver)}
                          disabled={!canInvite}
                          className={clsx(
                            "flex w-full items-center gap-3 px-2 py-3 text-left",
                            canInvite
                              ? clsx("hover:bg-limestone-50", isMissingContact(caregiver) && "bg-amber-50")
                              : "cursor-default opacity-50",
                          )}
                        >
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-globemallow-100 text-sm font-medium text-globemallow-700">
                            {caregiver.firstName[0]}
                            {caregiver.lastName[0]}
                          </div>
                          <div className="min-w-0 flex-1">
                            <Small displayType="loud" colorType="primary">
                              {caregiver.name}
                            </Small>
                            <Small colorType="secondary" className="text-xs">
                              {caregiver.linkedChildren.map((c) => c.name).join(", ") || "No linked child"}
                            </Small>
                            {canInvite && (
                              <>
                                {caregiver.email || caregiver.phone ? (
                                  <Small colorType="secondary" className="text-xs">
                                    {[caregiver.email, caregiver.phone].filter(Boolean).join(" · ")}
                                  </Small>
                                ) : (
                                  <Small className="text-xs text-amber-600">
                                    Missing contact info — you can still generate a code to share manually
                                  </Small>
                                )}
                              </>
                            )}
                          </div>
                          {label && <span className="shrink-0 text-xs font-medium text-text-secondary">{label}</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {caregiversFetchingMore && (
              <Small colorType="secondary" className="text-center text-xs">
                Loading more caregivers…
              </Small>
            )}

            <Button
              variant="primary"
              buttonStyle="ghost"
              text="Can't find them? Add manually"
              onClick={() => setStep(Step.Manual)}
            />
          </div>
        )}

        {/* Step 1b: Add manually */}
        {step === Step.Manual && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              <Label displayType="loud" colorType="secondary" size="extra-small">
                Caregiver
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  name="caregiverFirstName"
                  label="First name"
                  formNoValidate
                  inputSize="full"
                  placeholder="First name"
                  value={manualForm.caregiverFirstName}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, caregiverFirstName: e.target.value }))}
                />
                <Input
                  name="caregiverLastName"
                  label="Last name"
                  formNoValidate
                  inputSize="full"
                  placeholder="Last name"
                  value={manualForm.caregiverLastName}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, caregiverLastName: e.target.value }))}
                />
              </div>
              <Input
                name="caregiverEmail"
                label="Email"
                type="email"
                formNoValidate
                inputSize="full"
                placeholder="Email"
                value={manualForm.email}
                onChange={(e) => setManualForm((prev) => ({ ...prev, email: e.target.value }))}
              />
              <Input
                name="caregiverPhone"
                label="Phone"
                type="tel"
                formNoValidate
                inputSize="full"
                placeholder="Phone"
                value={manualForm.phone}
                onChange={(e) => setManualForm((prev) => ({ ...prev, phone: e.target.value }))}
              />
              <CheckboxList
                key={`isPrimary-${childMode}-${selectedLearnerHasPrimaryCaregiver}`}
                items={[
                  {
                    id: "isPrimary",
                    title: selectedLearnerHasPrimaryCaregiver
                      ? "Primary caregiver (this child already has one)"
                      : "Primary caregiver",
                    defaultChecked: selectedLearnerHasPrimaryCaregiver ? false : manualForm.isPrimary,
                  },
                ]}
                title=""
                legend=""
                orientation="horizontal"
                disabled={childMode === ChildMode.Existing && selectedLearnerHasPrimaryCaregiver}
                onChange={(e) => setManualForm((prev) => ({ ...prev, isPrimary: e.target.checked }))}
              />
            </div>

            <div className="flex flex-col gap-3 border-t border-limestone-100 pt-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setChildMode(ChildMode.New);
                    setSelectedLearnerItem(null);
                    setSelectedProviders([]);
                  }}
                  className={
                    childMode === ChildMode.New
                      ? "rounded-full border border-rio-600 bg-rio-50 px-3 py-1 text-xs font-medium text-rio-700 transition-colors"
                      : "rounded-full border border-border-medium bg-white px-3 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-limestone-300 hover:bg-limestone-50"
                  }
                >
                  New child
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setChildMode(ChildMode.Existing);
                    setManualForm((prev) => ({ ...prev, learnerFirstName: "", learnerLastName: "", birthDate: "" }));
                    setSelectedProviders([]);
                  }}
                  className={
                    childMode === ChildMode.Existing
                      ? "rounded-full border border-rio-600 bg-rio-50 px-3 py-1 text-xs font-medium text-rio-700 transition-colors"
                      : "rounded-full border border-border-medium bg-white px-3 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-limestone-300 hover:bg-limestone-50"
                  }
                >
                  Existing child
                </button>
              </div>

              {childMode === ChildMode.New ? (
                <>
                  <Small colorType="secondary" className="text-xs">
                    Required to activate the caregiver&apos;s app access.
                  </Small>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      name="learnerFirstName"
                      label="First name"
                      formNoValidate
                      inputSize="full"
                      placeholder="First name"
                      value={manualForm.learnerFirstName}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, learnerFirstName: e.target.value }))}
                    />
                    <Input
                      name="learnerLastName"
                      label="Last name"
                      formNoValidate
                      inputSize="full"
                      placeholder="Last name"
                      value={manualForm.learnerLastName}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, learnerLastName: e.target.value }))}
                    />
                  </div>
                  <Input
                    name="learnerBirthDate"
                    label="Date of birth"
                    type="date"
                    formNoValidate
                    inputSize="full"
                    value={manualForm.birthDate}
                    onChange={(e) => setManualForm((prev) => ({ ...prev, birthDate: e.target.value }))}
                  />
                </>
              ) : (
                <Select
                  title="Existing child"
                  items={learnerItems}
                  placeholderText="Search by name"
                  selected={selectedLearnerItem}
                  setSelected={setSelectedLearnerItem}
                  enableSearch
                />
              )}
            </div>

            {(childMode === ChildMode.New || selectedLearner) && (
              <div className="flex flex-col gap-3 border-t border-limestone-100 pt-4">
                <Label displayType="loud" colorType="secondary" size="extra-small">
                  {childMode === ChildMode.Existing ? "Providers — includes any already assigned" : "Providers"}
                </Label>
                <MultiSelect
                  title="Assign providers"
                  items={assignableLearnerProviders}
                  placeholderText="available providers"
                  selected={selectedProviders}
                  setSelected={setSelectedProviders}
                  mode="tags"
                  isLoading={providersForAssignmentQuery.isLoading}
                />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="primary" buttonStyle="border" text="Back" onClick={() => setStep(Step.Select)} />
              <Button
                variant="primary"
                buttonStyle="filled"
                text="Next"
                onClick={handleManualNext}
                disabled={!isManualFormValid || isManualMutationPending}
              />
            </div>
          </div>
        )}

        {/* Step 2: Confirm & send */}
        {step === Step.Confirm && selectedCaregiver && (
          <div className="flex flex-col gap-4">
            {/* Summary card */}
            <div className="rounded-md border border-limestone-200 bg-limestone-50 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-globemallow-100 text-sm font-medium text-globemallow-700">
                  {selectedCaregiver.firstName[0]}
                  {selectedCaregiver.lastName[0]}
                </div>
                <div>
                  <Small displayType="loud" colorType="primary">
                    {selectedCaregiver.name}
                  </Small>
                  {selectedCaregiver.linkedChildren.length > 0 && (
                    <Small colorType="secondary" className="text-xs">
                      Linked to: {selectedCaregiver.linkedChildren.map((c) => c.name).join(", ")}
                    </Small>
                  )}
                  <Small colorType="secondary" className="text-xs">
                    {[selectedCaregiver.email, selectedCaregiver.phone].filter(Boolean).join(" · ") ||
                      "No contact info on file"}
                  </Small>
                </div>
              </div>
            </div>

            {/* Delivery method */}
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
              <Paragraph className="text-sm text-blue-800">
                {selectedCaregiver.appStatus === CaregiverAppStatus.CodeSent
                  ? "A new activation code will be resent to this caregiver."
                  : getDeliveryMethod(selectedCaregiver.email, selectedCaregiver.phone)}
              </Paragraph>
            </div>

            {/* Activation code */}
            <div className="rounded-md border border-limestone-200 p-4">
              <Label displayType="loud" colorType="secondary" size="extra-small" className="mb-2">
                Activation code
              </Label>
              <div className="flex items-center gap-3">
                <span className="rounded-md bg-limestone-100 px-4 py-2 font-mono text-lg font-bold tracking-widest text-text-primary">
                  {selectedCaregiver.activationCode}
                </span>
                <Button
                  variant="primary"
                  buttonStyle="border"
                  text={copied ? "Copied!" : "Copy"}
                  onClick={handleCopyCode}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="primary"
                buttonStyle="border"
                text="Back"
                onClick={handleBack}
                disabled={sendActivationCode.isPending}
              />
              <Button
                variant="primary"
                buttonStyle="filled"
                text={selectedCaregiver.appStatus === CaregiverAppStatus.CodeSent ? "Resend invite" : "Send invite"}
                onClick={handleSendInvite}
                disabled={sendActivationCode.isPending || isMissingContact(selectedCaregiver)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
